import React, { Component, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useHistory } from 'react-router-dom';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { createResourceLocatorString } from '../../../util/routes';
import { getSearchPageResourceLocatorStringParams } from '../SearchPage.shared';

import { ModalInMobile, Button } from '../../../components';

import ActiveFilterChips from '../ActiveFilterChips/ActiveFilterChips';
import CategoryBreadcrumb from '../CategoryBreadcrumb/CategoryBreadcrumb';
import PopupOpenerButton from '../PopupOpenerButton/PopupOpenerButton';
import IconChevronLeft from '../../LandingPage/sections/SectionListingStrip/icons/IconChevronLeft';
import IconChevronRight from '../../LandingPage/sections/SectionListingStrip/icons/IconChevronRight';
import css from './SearchFiltersMobile.module.css';

// Mirror SectionListingStrip's scroll behavior so the chip rail feels
// the same as the landing-page horizontal scrollers.
const SCROLL_BY_FRACTION = 0.85;
const SCROLL_EDGE_EPSILON = 1;

/**
 * Horizontally scrollable rail for the active-filter chips next to the
 * mobile Filter pill. Renders prev/next arrow buttons (matching the
 * landing-page strip) only when the content overflows. Re-checks edges
 * on scroll, on resize, and when chip count changes via `chipsKey`.
 */
const ChipsRail = ({ chipsKey, children }) => {
  const scrollerRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    const recompute = () => {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      const maxScroll = scrollWidth - clientWidth;
      setOverflows(maxScroll > SCROLL_EDGE_EPSILON);
      setCanScrollLeft(scrollLeft > SCROLL_EDGE_EPSILON);
      setCanScrollRight(scrollLeft < maxScroll - SCROLL_EDGE_EPSILON);
    };
    recompute();
    el.addEventListener('scroll', recompute, { passive: true });
    window.addEventListener('resize', recompute);
    return () => {
      el.removeEventListener('scroll', recompute);
      window.removeEventListener('resize', recompute);
    };
  }, [chipsKey]);

  const scrollBy = direction => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth * SCROLL_BY_FRACTION * direction, behavior: 'smooth' });
  };

  return (
    <div className={css.chipsRail}>
      <div className={css.activeChipsScroll} ref={scrollerRef}>
        {children}
      </div>
      {overflows ? (
        <>
          <button
            type="button"
            className={classNames(css.chipsArrow, css.chipsArrowPrev)}
            onClick={() => scrollBy(-1)}
            disabled={!canScrollLeft}
            aria-hidden="true"
            tabIndex={-1}
          >
            <IconChevronLeft className={css.chipsArrowIcon} />
          </button>
          <button
            type="button"
            className={classNames(css.chipsArrow, css.chipsArrowNext)}
            onClick={() => scrollBy(1)}
            disabled={!canScrollRight}
            aria-hidden="true"
            tabIndex={-1}
          >
            <IconChevronRight className={css.chipsArrowIcon} />
          </button>
        </>
      ) : null}
    </div>
  );
};

class SearchFiltersMobileComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { isFiltersOpenOnMobile: false, initialQueryParams: null };

    this.openFilters = this.openFilters.bind(this);
    this.cancelFilters = this.cancelFilters.bind(this);
    this.closeFilters = this.closeFilters.bind(this);
    this.resetAll = this.resetAll.bind(this);
  }

  // Open filters modal, set the initial parameters to current ones
  openFilters() {
    const { onOpenModal, urlQueryParams } = this.props;
    onOpenModal();
    this.setState({ isFiltersOpenOnMobile: true, initialQueryParams: urlQueryParams });
  }

  // Close the filters by clicking cancel, revert to the initial params
  cancelFilters() {
    const { history, onCloseModal, location, routeConfiguration } = this.props;

    const { routeName, pathParams } = getSearchPageResourceLocatorStringParams(
      routeConfiguration,
      location
    );

    history.push(
      createResourceLocatorString(
        routeName,
        routeConfiguration,
        pathParams,
        this.state.initialQueryParams
      )
    );
    onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false, initialQueryParams: null });
  }

  // Close the filter modal
  closeFilters() {
    this.props.onCloseModal();
    this.setState({ isFiltersOpenOnMobile: false });
  }

  // Reset all filter query parameters
  resetAll(e) {
    this.props.resetAll(e);

    // blur event target if event is passed
    if (e && e.currentTarget) {
      e.currentTarget.blur();
    }
  }

  render() {
    const {
      rootClassName,
      className,
      children,
      sortByComponent,
      listingsAreLoaded,
      resultsCount,
      searchInProgress = false,
      showAsModalMaxWidth,
      onMapIconClick = () => {},
      onManageDisableScrolling,
      selectedFiltersCount = 0,
      noResultsInfo,
      intl,
      isMapVariant = true,
      urlQueryParams,
      availableFilters,
    } = this.props;

    const classes = classNames(rootClassName || css.root, className);

    const resultsFound = (
      <FormattedMessage id="SearchFiltersMobile.foundResults" values={{ count: resultsCount }} />
    );
    const noResults = <FormattedMessage id="SearchFiltersMobile.noResults" />;
    const loadingResults = <FormattedMessage id="SearchFiltersMobile.loadingResults" />;
    const filtersHeading = intl.formatMessage({ id: 'SearchFiltersMobile.heading' });
    const modalCloseButtonMessage = intl.formatMessage({ id: 'SearchFiltersMobile.cancel' });

    const showListingsLabel = intl.formatMessage(
      { id: 'SearchFiltersMobile.showListings' },
      { count: resultsCount }
    );

    const filterIcon = (
      <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
        className={css.filterIcon}
      >
        <path
          d="M1 3h12M3 7h8M5 11h4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );

    return (
      <div className={classes}>
        {/* Top row — Filter pill (matches Figma 235:589). The legacy
            map button is gated behind isMapVariant which the redesigned
            grid SearchPage never sets. */}
        <div className={css.buttons}>
          <PopupOpenerButton isSelected={selectedFiltersCount > 0} toggleOpen={this.openFilters}>
            {filterIcon}
            <FormattedMessage id="SearchFiltersMobile.filtersButtonLabel" />
            {selectedFiltersCount > 0 ? <span> ({selectedFiltersCount})</span> : null}
          </PopupOpenerButton>
          <ChipsRail chipsKey={JSON.stringify(urlQueryParams || {})}>
            <ActiveFilterChips
              urlQueryParams={urlQueryParams}
              availableFilters={availableFilters}
            />
          </ChipsRail>
          {isMapVariant ? (
            <div className={css.mapIcon} onClick={onMapIconClick}>
              <FormattedMessage id="SearchFiltersMobile.openMapView" className={css.mapIconText} />
            </div>
          ) : null}
        </div>
        {/* Bottom row — result count on the left, Sort pill on the
            right (Figma 235:589). */}
        <div className={css.searchResultSummary}>
          <span className={css.resultsLine}>
            {listingsAreLoaded && resultsCount > 0 ? resultsFound : null}
            {listingsAreLoaded && resultsCount === 0 ? noResults : null}
            {searchInProgress ? loadingResults : null}
          </span>
          {sortByComponent}
        </div>

        {noResultsInfo ? noResultsInfo : null}

        <ModalInMobile
          id="SearchFiltersMobile.filters"
          isModalOpenOnMobile={this.state.isFiltersOpenOnMobile}
          onClose={this.cancelFilters}
          showAsModalMaxWidth={showAsModalMaxWidth}
          onManageDisableScrolling={onManageDisableScrolling}
          containerClassName={css.modalContainer}
          closeButtonMessage={modalCloseButtonMessage}
          // Portal-mount so the modal escapes the listing-grid
          // stacking context and the FavoriteButton hearts on cards
          // can't bleed through on top of the filters.
          usePortal
        >
          <div className={css.modalHeadingWrapper}>
            <span className={css.modalHeading}>{filtersHeading}</span>
            <button className={css.resetAllButton} onClick={e => this.resetAll(e)}>
              <FormattedMessage id={'SearchFiltersMobile.resetAll'} />
            </button>
          </div>
          {this.state.isFiltersOpenOnMobile ? (
            <div className={css.filtersWrapper}>
              <CategoryBreadcrumb />
              <ActiveFilterChips
                urlQueryParams={urlQueryParams}
                availableFilters={availableFilters}
              />
              {children}
            </div>
          ) : null}

          <div className={css.showListingsContainer}>
            <Button className={css.showListingsButton} onClick={this.closeFilters}>
              {showListingsLabel}
            </Button>
          </div>
        </ModalInMobile>
      </div>
    );
  }
}

/**
 * SearchFiltersMobile component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Object} props.urlQueryParams - The URL query params
 * @param {React.Node} props.sortByComponent - The sort by component
 * @param {boolean} props.listingsAreLoaded - Whether the listings are loaded
 * @param {number} props.resultsCount - The number of results
 * @param {boolean} props.searchInProgress - Whether the search is in progress
 * @param {number} props.showAsModalMaxWidth - The maximum width of the modal
 * @param {Function} props.onMapIconClick - The function to click the map icon
 * @param {Function} props.onManageDisableScrolling - The function to manage disable scrolling
 * @param {Function} props.onOpenModal - The function to open the modal
 * @param {Function} props.onCloseModal - The function to close the modal
 * @param {Function} props.resetAll - The function to reset all
 * @param {number} props.selectedFiltersCount - The number of selected filters
 * @param {boolean} props.isMapVariant - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchFiltersMobile = props => {
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();

  return (
    <SearchFiltersMobileComponent
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      {...props}
    />
  );
};

export default SearchFiltersMobile;
